/**
 * PR-E1 — runtime SDF atlas loader.
 *
 * Fetches a pre-built atlas pair (PNG + JSON metrics) from
 * `/sdf-atlases/${fontName}.{png,json}` and returns a parsed
 * `SdfAtlas` ready for the (still-to-land) custom SDF text shader
 * (PR-E2 / `textMaterial.ts`).
 *
 * # Atlas binary contract
 *
 * The atlas PNG / JSON pair is produced by `scripts/build-sdf-atlas.sh`
 * (Q22.A msdf pipeline + post-processor `build-sdf-atlas-postprocess.mjs`).
 * The post-processor pins the JSON shape:
 *
 * ```jsonc
 * {
 *   "fontName": "Roboto-Regular",
 *   "atlasSize": 1024,         // square atlas; texture w === h
 *   "lineHeight": 55,          // baseline-to-baseline (px @ font size)
 *   "distanceRange": 4,        // msdf range (px) — feeds shader uniform
 *   "glyphs": {
 *     "65": { "u": 13, "v": 96, "w": 28, "h": 33,
 *             "advance": 26, "bearingX": 1, "bearingY": 24 },
 *     ...
 *   }
 * }
 * ```
 *
 *   - `u, v, w, h` — atlas pixel coords (top-left origin per BMFont
 *     convention; loader divides by `atlasSize` for UV space).
 *   - `advance` — horizontal advance to the next glyph (px @ font size).
 *   - `bearingX, bearingY` — baseline-relative offset of the glyph
 *     bounding box (pre-converted from BMFont yoffset to baseline-up).
 *
 * # Caching (PR-E1 §3)
 *
 * One promise per font name, kept for the lifetime of the page. A
 * concurrent second `loadSdfAtlas("Roboto-Regular")` while the first
 * is still in-flight returns the same promise — no duplicate fetches.
 * `evictAtlasCache` is exposed for tests.
 *
 * # Error path
 *
 * If either fetch returns non-OK or the JSON is malformed, the
 * loader rejects with a `MissingAtlasError`. PR-E2's
 * `textRunRenderer.ts` catches this and falls back to "?????"
 * placeholder rectangles per Q15.C.
 *
 * # CLAUDE.md Приоритет #1 — full fidelity
 *
 * No simplifications: every glyph metric BMFont produces is preserved.
 * The shader does its own LOD / culling — the loader is a pure data
 * pipe.
 */

import * as THREE from "three";

// ── Public types ─────────────────────────────────────────────────

/**
 * Per-glyph metric record. Pixel-space (relative to atlas size and
 * font size); the runtime renderer scales by `runHeight / lineHeight`.
 */
export interface GlyphMetric {
  /** Atlas pixel x of glyph top-left. */
  u: number;
  /** Atlas pixel y of glyph top-left. */
  v: number;
  /** Glyph width in atlas pixels. */
  w: number;
  /** Glyph height in atlas pixels. */
  h: number;
  /** Horizontal advance to the next glyph (px). */
  advance: number;
  /** Baseline-relative x bearing of the glyph (px). */
  bearingX: number;
  /** Baseline-relative y bearing of glyph top (+Y up, px). */
  bearingY: number;
}

/**
 * Loaded atlas — owns its `THREE.Texture`. Disposing the texture is
 * the caller's responsibility (Scene3DStage.tsx unmount path).
 *
 * `glyphs` is a `Map<number, GlyphMetric>` keyed by Unicode codepoint
 * (number, not string) so the hot per-frame path avoids `parseInt`.
 */
export interface SdfAtlas {
  fontName: string;
  /** Square atlas pixel dim — UVs = u/atlasSize, v/atlasSize. */
  atlasSize: number;
  lineHeight: number;
  distanceRange: number;
  texture: THREE.Texture;
  glyphs: Map<number, GlyphMetric>;
}

/**
 * Raw on-disk shape (matches the JSON emitted by
 * `scripts/build-sdf-atlas-postprocess.mjs`). Internal only — callers
 * receive the parsed `SdfAtlas` with the texture attached and the
 * glyph dictionary inflated to `Map<number, GlyphMetric>`.
 */
interface AtlasJsonShape {
  fontName: string;
  atlasSize: number;
  lineHeight: number;
  distanceRange: number;
  glyphs: Record<string, GlyphMetric>;
}

/** Raised when the atlas pair is missing or malformed. */
export class MissingAtlasError extends Error {
  readonly fontName: string;
  readonly cause?: unknown;
  constructor(fontName: string, message: string, cause?: unknown) {
    super(`[sdfAtlasLoader] ${fontName}: ${message}`);
    this.name = "MissingAtlasError";
    this.fontName = fontName;
    if (cause !== undefined) this.cause = cause;
  }
}

// ── Cache (one in-flight promise per fontName) ────────────────────

const cache = new Map<string, Promise<SdfAtlas>>();

/**
 * Where atlases live on the static asset server. Kept as a constant
 * so tests can mock fetch with a stable URL prefix.
 */
export const SDF_ATLAS_BASE_PATH = "/sdf-atlases";

// ── Public API ───────────────────────────────────────────────────

/**
 * Load (or return the cached promise of) an SDF atlas for `fontName`.
 *
 * Thread-safe: concurrent calls coalesce; the second caller observes
 * the same promise as the first, so the atlas is fetched once per
 * session.
 *
 * @throws {MissingAtlasError} if either side of the pair fails to
 *   load or the JSON does not match the contract.
 */
export function loadSdfAtlas(fontName: string): Promise<SdfAtlas> {
  const existing = cache.get(fontName);
  if (existing) return existing;

  const promise = fetchAndBuildAtlas(fontName).catch((err) => {
    // Drop the failed promise from the cache so a later call can retry
    // (e.g., after the dev fixed the missing source). Without this an
    // initial 404 would poison the cache for the page lifetime.
    cache.delete(fontName);
    throw err;
  });
  cache.set(fontName, promise);
  return promise;
}

/**
 * Test-only: clear all cached atlas promises. Production code MUST NOT
 * call this — atlas textures take ~1 MB GPU each and re-fetching
 * trashes the cache benefit.
 */
export function evictAtlasCache(): void {
  cache.clear();
}

/**
 * Test-only: peek at whether an atlas is cached without forcing a
 * fetch. `false` for "not cached" or "previous attempt failed".
 */
export function isAtlasCached(fontName: string): boolean {
  return cache.has(fontName);
}

// ── Internal ──────────────────────────────────────────────────────

async function fetchAndBuildAtlas(fontName: string): Promise<SdfAtlas> {
  const jsonUrl = `${SDF_ATLAS_BASE_PATH}/${encodeURIComponent(fontName)}.json`;
  const pngUrl = `${SDF_ATLAS_BASE_PATH}/${encodeURIComponent(fontName)}.png`;

  // Fetch JSON metrics first — if they're missing the atlas can't be
  // used at all. We could parallelise the PNG fetch with `Promise.all`
  // for marginal latency wins; PR-E1 keeps the sequence simple and
  // skips the wasted PNG decode on the 404 path.
  let raw: AtlasJsonShape;
  try {
    const res = await fetchOrThrow(jsonUrl, fontName);
    raw = (await res.json()) as AtlasJsonShape;
  } catch (err) {
    if (err instanceof MissingAtlasError) throw err;
    throw new MissingAtlasError(fontName, `failed to parse JSON metrics`, err);
  }

  validateAtlasJsonShape(fontName, raw);

  const texture = await loadAtlasTexture(pngUrl, fontName);

  const glyphs = new Map<number, GlyphMetric>();
  for (const [code, metric] of Object.entries(raw.glyphs)) {
    const codepoint = Number.parseInt(code, 10);
    if (!Number.isFinite(codepoint)) continue;
    glyphs.set(codepoint, metric);
  }

  return {
    fontName: raw.fontName,
    atlasSize: raw.atlasSize,
    lineHeight: raw.lineHeight,
    distanceRange: raw.distanceRange,
    texture,
    glyphs,
  };
}

async function fetchOrThrow(url: string, fontName: string): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url);
  } catch (err) {
    throw new MissingAtlasError(fontName, `network error fetching ${url}`, err);
  }
  if (!res.ok) {
    throw new MissingAtlasError(fontName, `HTTP ${res.status} fetching ${url}`);
  }
  return res;
}

function validateAtlasJsonShape(
  fontName: string,
  raw: unknown,
): asserts raw is AtlasJsonShape {
  if (!raw || typeof raw !== "object") {
    throw new MissingAtlasError(fontName, "JSON metrics root is not an object");
  }
  const r = raw as Record<string, unknown>;
  if (typeof r.fontName !== "string") {
    throw new MissingAtlasError(fontName, "missing/invalid fontName");
  }
  if (typeof r.atlasSize !== "number" || r.atlasSize <= 0) {
    throw new MissingAtlasError(fontName, "missing/invalid atlasSize");
  }
  if (typeof r.lineHeight !== "number" || r.lineHeight <= 0) {
    throw new MissingAtlasError(fontName, "missing/invalid lineHeight");
  }
  if (typeof r.distanceRange !== "number" || r.distanceRange <= 0) {
    throw new MissingAtlasError(fontName, "missing/invalid distanceRange");
  }
  if (!r.glyphs || typeof r.glyphs !== "object") {
    throw new MissingAtlasError(fontName, "missing/invalid glyphs map");
  }
}

/**
 * Load the atlas PNG into a `THREE.Texture`. We deliberately do NOT
 * use `THREE.TextureLoader` — its onError path calls `console.error`
 * but resolves with an empty texture, which would silently produce
 * black quads in the shader. Instead, we manually manage the Image +
 * fetch HEAD check so a 404 surfaces as a `MissingAtlasError` and the
 * caller can fall back to the "?????" placeholder cleanly.
 */
async function loadAtlasTexture(
  url: string,
  fontName: string,
): Promise<THREE.Texture> {
  // HEAD check — fetch is the only spec-compliant way to test 404 in
  // browsers. `Image.onerror` does fire on 404, but doesn't expose the
  // status code so we can't differentiate a network failure from a
  // missing file in the error message.
  let blob: Blob;
  try {
    const res = await fetchOrThrow(url, fontName);
    blob = await res.blob();
  } catch (err) {
    if (err instanceof MissingAtlasError) throw err;
    throw new MissingAtlasError(fontName, `failed to fetch atlas PNG`, err);
  }

  const img = await blobToImage(blob, fontName);

  const tex = new THREE.Texture(img);
  // SDF atlases must NOT be premultiplied (PR-E2 shader assumes
  // straight RGBA channels) and they must NOT generate mipmaps (the
  // SDF technique handles minification analytically — mipmaps blur
  // the distance field and produce fuzzy edges).
  tex.premultiplyAlpha = false;
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  // Wrapping defaults to ClampToEdgeWrapping; explicit for clarity —
  // out-of-bounds UV samples must NOT wrap into a neighbour glyph.
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  // Three.js will not flip Y on Image-source textures by default since
  // r137 — but BMFont atlases use top-left origin, matching DOM Image
  // origin, so flipY=false is correct (and we want it explicit).
  tex.flipY = false;
  tex.needsUpdate = true;
  tex.name = `sdf-atlas:${fontName}`;
  return tex;
}

/**
 * Decode a PNG blob into an HTMLImageElement (or, in test/jsdom land,
 * a stub object the texture constructor accepts). We use createImageBitmap
 * when available — it's faster on modern browsers and offloads decoding
 * to a worker — and fall back to the Image() constructor for jsdom.
 */
function blobToImage(
  blob: Blob,
  fontName: string,
): Promise<HTMLImageElement | ImageBitmap> {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(blob).catch((err) => {
      throw new MissingAtlasError(
        fontName,
        "createImageBitmap failed decoding atlas PNG",
        err,
      );
    });
  }
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(
        new MissingAtlasError(
          fontName,
          "Image() failed decoding atlas PNG",
          err,
        ),
      );
    };
    img.src = url;
  });
}
